import { Shield, Scale, FileText, TrendingUp, CheckCircle, Building2, ClipboardCheck, FileCheck, Clock, Edit3, XCircle, Landmark } from "lucide-react";
import { useNavigate } from "react-router-dom";

const ServicesPage = () => {
  const navigate = useNavigate();

  const mainServices = [
    {
      icon: Building2,
      title: "RERA Registration",
      description: "Complete end-to-end assistance with RERA registration for your real estate projects. We handle all documentation, filing, and follow-ups to ensure smooth approval.",
      features: [
        "Project registration assistance",
        "Compliance documentation",
        "Regular updates and renewals",
        "Expert guidance throughout",
      ],
    },
    {
      icon: Clock,
      title: "Extension Application",
      description: "Comprehensive support for RERA Project Extension applications to help developers obtain timely approval from the Authority and maintain regulatory compliance. We manage the complete process from documentation to final submission.",
      features: [
        "Preparation & filing of extension application",
        "Delay explanation and supporting documentation",
        "CA, Architect & Engineer certificate coordination",
        "Compliance review before submission",
        "Authority follow-ups and query handling",
      ],
    },
    {
      icon: Edit3,
      title: "Correction Application",
      description: "Professional assistance for correction and amendment applications in registered RERA projects. We ensure accurate updates in project details and seamless approval processing.",
      features: [
        "Project detail amendments and corrections",
        "Promoter profile and document updates",
        "Unit, carpet area & layout detail corrections",
        "Uploading revised approvals and documents",
        "End-to-end drafting, filing & follow-ups",
      ],
    },
    {
      icon: XCircle,
      title: "De-registration",
      description: "Professional assistance for RERA Project De-registration with complete documentation, compliance review, and authority coordination. We ensure proper filing and smooth processing of the de-registration application.",
      features: [
        "Preparation & filing of De-registration application",
        "Drafting of declarations, undertakings & supporting documents",
        "Review and resolution of pending compliances",
        "Coordination for authority queries and clarifications",
        "End-to-end submission and follow-ups with RERA Authority",
      ],
    },
    {
      icon: Landmark,
      title: "Project Closure / Bank Account Closure",
      description: "Complete assistance for RERA Project Closure and designated bank account closure with proper compliance management and authority coordination. We ensure smooth completion of the closure process through accurate documentation and timely submissions.",
      features: [
        "Preparation & filing of Project Closure application",
        "Uploading Occupancy Certificate (OC) and completion documents",
        "Assistance for designated RERA bank account closure",
        "CA certification and financial compliance support",
        "Resolution of authority queries and compliance issues",
        "End-to-end submission and follow-ups",
      ],
    },
    {
      icon: Scale,
      title: "Legal Compliance",
      description: "Comprehensive legal compliance services to keep your business aligned with all regulatory requirements and avoid penalties.",
      features: [
        "Regulatory compliance audit",
        "Legal framework setup",
        "Ongoing compliance monitoring",
        "Agreement for Sale Overview",
        "Search Report Overview",
        "All Legal Documentation",
      ],
    },
    {
      icon: FileText,
      title: "Documentation Services",
      description: "Professional documentation services for all your business, legal, and compliance needs with attention to detail and accuracy.",
      features: [
        "Business agreement drafting",
        "Contract review and vetting",
        "Legal notice preparation",
        "Corporate documentation",
        "Update Financial Encumbrance",
        "Update Legal Encumbrance",
      ],
    },
    {
      icon: TrendingUp,
      title: "Business Advisory",
      description: "Strategic advisory services to help you make informed business decisions, optimize operations, and achieve sustainable growth.",
      features: [
        "Business strategy consulting",
        "Market analysis and research",
        "Financial planning guidance",
        "Growth and expansion support",
      ],
    },
  ];

  const complianceServices = [
    {
      icon: ClipboardCheck,
      title: "QPR (Quarterly Progress Report) Compliances",
      description: "Mandatory quarterly reporting to RERA authorities ensuring project transparency and regulatory compliance.",
      items: [
        "Form 1 - Architect Certificate",
        "Form 2 - Engineer Certificate",
        "Form 3 - CA Certificate",
      ],
    },
    {
      icon: FileCheck,
      title: "APR (Annual Progress Report) Compliances",
      description: "Annual compliance reporting ensuring continuous adherence to RERA regulations and project milestones.",
      items: [
        "Form 2A - Annual Engineer Certificate",
        "Form 5 - Annual CA Certificate",
      ],
    },
  ];

  return (
    <div className="min-h-screen fade-in" data-testid="services-page">
      {/* Hero Section */}
      <section
        className="pt-32 pb-20 md:py-32 section-light"
        data-testid="services-hero-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <h1
            className="text-5xl md:text-6xl tracking-tight leading-tight text-[#1A1A1A] mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="services-hero-heading"
          >
            Our Services
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-[#555555] max-w-3xl">
            Comprehensive RERA compliance and advisory solutions tailored to your business needs. From registration to quarterly reporting, we've got you covered.
          </p>
        </div>
      </section>

      {/* Main Services Grid */}
      <section className="py-20 md:py-32 section-light" data-testid="main-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Comprehensive Business Solutions for End-to-End RERA Compliance & Advisory
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {mainServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="service-card bg-white p-10 rounded-lg shadow-sm border border-slate-100"
                  data-testid={`main-service-card-${index}`}
                >
                  <Icon className="w-14 h-14 text-[#C9A14A] mb-6" />
                  <h3
                    className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#555555] mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-[#C9A14A] mt-0.5 flex-shrink-0" />
                        <span className="text-[#555555]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QPR & APR Compliance Section */}
      <section className="py-20 md:py-32 section-grey" data-testid="compliance-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              QPR & APR Compliances
            </h2>
            <p className="text-lg leading-relaxed text-[#555555] max-w-2xl mx-auto">
              Stay compliant with mandatory reporting requirements through our expert QPR and APR services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {complianceServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-10 rounded-lg shadow-sm border border-slate-100"
                  data-testid={`compliance-card-${index}`}
                >
                  <Icon className="w-14 h-14 text-[#C9A14A] mb-6" />
                  <h3
                    className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-4"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-base leading-relaxed text-[#555555] mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-md">
                        <CheckCircle className="w-5 h-5 text-[#C9A14A] flex-shrink-0" />
                        <span className="text-[#1A1A1A] font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 section-light" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="why-choose-heading"
            >
              Why Choose RERA Hub
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* 5+ Years of Expertise */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="why-expertise">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FDF8F0] flex items-center justify-center">
                <svg className="w-7 h-7 text-[#C9A14A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">5+ Years of Expertise</h3>
              <p className="text-[#555555] text-sm">
                Proven track record in delivering exceptional RERA compliance and advisory services.
              </p>
            </div>

            {/* Expert Professional Team */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="why-team">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FDF8F0] flex items-center justify-center">
                <svg className="w-7 h-7 text-[#C9A14A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">Expert Professional Team</h3>
              <p className="text-[#555555] text-sm">
                Dedicated professionals with deep industry knowledge and experience.
              </p>
            </div>

            {/* Timely Approvals & Filings */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="why-fast">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FDF8F0] flex items-center justify-center">
                <svg className="w-7 h-7 text-[#C9A14A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">Timely Approvals & Filings</h3>
              <p className="text-[#555555] text-sm">
                We ensure quick registration, extensions, and QPR filings without delays.
              </p>
            </div>

            {/* Accurate Documentation */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="why-documentation">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FDF8F0] flex items-center justify-center">
                <svg className="w-7 h-7 text-[#C9A14A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">Accurate Documentation</h3>
              <p className="text-[#555555] text-sm">
                Precision driven documentation to avoid rejections, notices, and penalties.
              </p>
            </div>

            {/* 100% Transparency */}
            <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 text-center" data-testid="why-transparent">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FDF8F0] flex items-center justify-center">
                <svg className="w-7 h-7 text-[#C9A14A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">100% Transparency</h3>
              <p className="text-[#555555] text-sm">
                Clear communication and complete visibility at every stage of the process.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 section-grey border-t-4 border-[#C9A14A]" data-testid="services-cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="bg-white p-12 rounded-lg shadow-sm border border-slate-100">
            <h2
              className="text-3xl md:text-5xl tracking-tight leading-snug text-[#1A1A1A] mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
              data-testid="services-cta-heading"
            >
              Ready to Ensure RERA Compliance?
            </h2>
            <p className="text-lg leading-relaxed text-[#555555] mb-8 max-w-2xl mx-auto">
              Get in touch with us today to discuss how we can help your business achieve compliance excellence and sustainable growth.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="btn-accent px-8 py-4 rounded-md font-medium text-lg"
              data-testid="services-cta-button"
            >
              Contact Us Now
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
